import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import { useDynamoUtils } from '../context/DynamoUtilsContext';

/**
 * Custom hook to track session progress across different modules and difficulties
 * This hook uses DynamoDB to fetch previous sessions and organize them by module and difficulty
 */
export const usePersistentSession = (userProfile) => {
  const [sessionProgress, setSessionProgress] = useState({});
  const [isLoadingProgress, setIsLoadingProgress] = useState(true);
  const [error, setError] = useState(null);
  const { user, getTemporaryCredentials } = useAuth();
  const userId = user?.sub || null;
  const dynamoUtilsRef = useDynamoUtils();
  
  // Use a ref to track initialization attempts and prevent infinite loops
  const initializationAttempts = useRef(0);
  const retryTimeoutRef = useRef(null);

  // Fetch all sessions and organize them by module and difficulty
  useEffect(() => {
    const fetchSessions = async () => {
      if (!userId) {
        setIsLoadingProgress(false);
        return;
      }

      setIsLoadingProgress(true);
      setError(null);

      // Add a timeout to prevent indefinite loading
      const timeoutId = setTimeout(() => {
        console.warn('Session progress loading timed out after 8 seconds');
        setIsLoadingProgress(false);
        setError('Loading sessions timed out. Try refreshing the page.');
      }, 8000);

      try {
        if (!dynamoUtilsRef?.current) {
          setIsLoadingProgress(false);
          clearTimeout(timeoutId);
          return;
        }

        console.log('Using DynamoDB utils:', dynamoUtilsRef.current);
        
        // Fetch session metadata from DynamoDB
        const metaItems = await dynamoUtilsRef.current.getUserConversationsMeta(userId);
        
        // Process the sessions into a structure organized by module and difficulty
        const progressMap = {};
        
        metaItems.forEach(session => {
          // Skip if missing required metadata
          if (!session?.meta?.topic && !session?.content) {
            console.log('Skipping session with missing metadata:', session.conversation_id);
            return;
          }
          
          // Parse session content if it's a string
          let sessionMeta = session.meta || {};
          if (typeof session.content === 'string' && session.content.startsWith('{')) {
            try {
              sessionMeta = { ...sessionMeta, ...JSON.parse(session.content) };
            } catch (e) {
              console.warn('Failed to parse session content:', e);
            }
          }
          
          // Extract moduleId from the meta - we need to ensure all sessions have this
          const moduleId = sessionMeta.moduleId || sessionMeta.id || session.conversation_id;
          
          // Initialize nested objects if they don't exist
          if (!progressMap[moduleId]) {
            progressMap[moduleId] = {};
          }
          
          const difficulty = sessionMeta.difficulty || 'medium';
          
          // Check for the status field that we're now storing in DynamoDB
          const status = sessionMeta.status || 'in_progress';
          const isCompleted = sessionMeta.completed === true || status === 'completed';
          const isActive = !isCompleted;
          const lastUpdated = session.timestamp || Date.now();
          // Store the session info or update if more recent
          if (!progressMap[moduleId][difficulty] || progressMap[moduleId][difficulty].lastUpdated < lastUpdated) {
            progressMap[moduleId][difficulty] = {
              sessionId: session.conversation_id,
              completed: isCompleted,
              active: isActive,
              status: status, // Add status field to the session progress object
              lastUpdated
            };
          }
        });
        setSessionProgress(progressMap);
        clearTimeout(timeoutId); // Clear the timeout on success
      } catch (err) {
        console.error('Error loading session progress:', err);
        
        // If we get a credentials error, retry with backoff
        if (err.message && (err.message.includes('credentials') || err.message.includes('permission'))) {
          initializationAttempts.current += 1;
          
          if (initializationAttempts.current <= 3) {
            console.log(`Will retry after credentials error (attempt ${initializationAttempts.current} of 3) in ${initializationAttempts.current * 2} seconds`);
            
            if (retryTimeoutRef.current) {
              clearTimeout(retryTimeoutRef.current);
            }
            
            retryTimeoutRef.current = setTimeout(() => {
              console.log('Retrying after credentials error...');
              fetchSessions();
            }, initializationAttempts.current * 2000);
            
            clearTimeout(timeoutId);
            return;
          }
        }
        
        setError('Failed to load previous sessions');
      } finally {
        clearTimeout(timeoutId); // Clear timeout to be safe
        setIsLoadingProgress(false);
      }
    };

    fetchSessions();
    
    // Add a fallback timeout for the entire hook
    const globalTimeoutId = setTimeout(() => {
      setIsLoadingProgress(false);
    }, 10000);
    
    return () => {
      clearTimeout(globalTimeoutId);
      // Clean up any retry timeouts
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [userId, getTemporaryCredentials]);

  // Function to update progress when a session completes or updates
  const updateSessionProgress = (moduleId, difficulty, sessionId, isCompleted = false, status = null) => {
    setSessionProgress(prev => {
      const updated = { ...prev };
      
      if (!updated[moduleId]) {
        updated[moduleId] = {};
      }
      
      // Determine status based on completion flag if not explicitly provided
      const sessionStatus = status || (isCompleted ? 'completed' : 'in_progress');
      
      updated[moduleId][difficulty] = {
        sessionId,
        completed: isCompleted,
        active: !isCompleted,
        status: sessionStatus,
        lastUpdated: Date.now()
      };
      
      return updated;
    });
  };

  return {
    sessionProgress,
    isLoadingProgress,
    error,
    updateSessionProgress
  };
};

export default usePersistentSession;